import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { RideDTO } from '@/api/generated/types';
import { formatNumber, parseInstant } from '@/lib/format';

interface Props {
  ride: Pick<RideDTO, 'start' | 'end' | 'minutes'>;
  children?(minutes: number): ReactNode;
}

const MINUTE = 60_000;
const display = (minutes: number, children: Props['children']) => children ? children(minutes) : formatNumber(minutes);

/** Numeric value matching RideMinutes, for header ordering of active and ended rides. */
export function rideMinutesValue(ride: Props['ride']): number | null | undefined {
  if (ride.end) return ride.minutes;
  const start = parseInstant(ride.start)?.getTime();
  return start == null ? null : Math.max(0, Math.floor((Date.now() - start) / MINUTE));
}

/** Display elapsed whole minutes while open (including pauses), and the server's final minutes once ended. */
export function RideMinutes({ ride, children }: Props) {
  const { t } = useTranslation('rides');
  const start = parseInstant(ride.start)?.getTime();
  return (
    <span className="tabular-nums" title={!ride.end ? t('elapsedMinutesHint') : undefined}>
      {ride.end ? (
        ride.minutes != null && Number.isFinite(ride.minutes) ? display(ride.minutes, children) : '—'
      ) : start == null ? '—' : (
        <ElapsedMinutes key={start} start={start}>{children}</ElapsedMinutes>
      )}
    </span>
  );
}

/** Only open rides run a timer. Tick at their minute boundary and catch up after a background tab resumes. */
function ElapsedMinutes({ start, children }: { start: number; children: Props['children'] }) {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const update = () => {
      clearTimeout(timer);
      const current = Date.now();
      setNow(current);
      // Positive modulo also handles a start slightly ahead of the browser clock.
      timer = setTimeout(update, MINUTE - ((current - start) % MINUTE + MINUTE) % MINUTE);
    };
    const onVisible = () => { if (document.visibilityState === 'visible') update(); };
    update();
    window.addEventListener('focus', update);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('focus', update);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [start]);
  return display(Math.max(0, Math.floor((now - start) / MINUTE)), children);
}
